<template>
  <div class="container" style="margin-top: 100px">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card">
          <div class="card-body">
            <h2 class="card-title mb-4 text-center">{{ $t("profile.title") }}</h2>

            <form @submit.prevent="updateProfile">
              <!-- Personal Information -->
              <div class="mb-4">
                <h4 class="h5 mb-3">{{ $t("profile.personalInfo.title") }}</h4>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">{{
                      $t("profile.personalInfo.firstName")
                    }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="profile.firstName"
                      required
                    />
                  </div>
                  <div class="col-md-6">
                    <label class="form-label">{{
                      $t("profile.personalInfo.lastName")
                    }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="profile.lastName"
                      required
                    />
                  </div>
                  <div class="col-12">
                    <label class="form-label">{{
                      $t("profile.personalInfo.email")
                    }}</label>
                    <input
                      type="email"
                      class="form-control"
                      v-model="profile.email"
                      required
                      disabled
                    />
                  </div>
                  <div class="col-12">
                    <label class="form-label">{{
                      $t("profile.personalInfo.companyName")
                    }}</label>
                    <input
                      type="text"
                      class="form-control"
                      v-model="profile.companyName"
                    />
                  </div>
                </div>
              </div>

              <!-- Change Password -->
              <div class="mb-4">
                <h4 class="h5 mb-3">
                  {{ $t("profile.changePassword.title") }}
                </h4>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label">{{
                      $t("profile.changePassword.currentPassword")
                    }}</label>
                    <input
                      type="password"
                      class="form-control"
                      v-model="passwordChange.currentPassword"
                    />
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label">{{
                        $t("profile.changePassword.newPassword")
                      }}</label>
                      <input
                        type="password"
                        class="form-control"
                        v-model="passwordChange.newPassword"
                      />
                    </div>
                    <div>
                      <label class="form-label">{{
                        $t("profile.changePassword.confirmPassword")
                      }}</label>
                      <input
                        type="password"
                        class="form-control"
                        v-model="passwordChange.confirmPassword"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Error Message -->
              <div v-if="error" class="alert alert-danger" role="alert">
                {{ error }}
              </div>

              <!-- Success Message -->
              <div v-if="success" class="alert alert-success" role="alert">
                {{ success }}
              </div>

              <!-- Submit Button -->
              <div class="d-flex justify-content-end">
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="loading"
                >
                  {{
                    loading
                      ? $t("profile.buttons.saving")
                      : $t("profile.buttons.saveChanges")
                  }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed } from "vue";
import { useAuthStore } from "@/stores/auth";
import { storeToRefs } from "pinia";
import api from "@/plugins/api";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "ProfileView",
  setup() {
    const { t } = useI18n();
    const authStore = useAuthStore();
    const { user } = storeToRefs(authStore);
    const loading = ref(false);
    const error = ref("");
    const success = ref("");

    // Initialize profile with computed values from the store
    const profile = ref({
      firstName: user.value?.first_name || "",
      lastName: user.value?.last_name || "",
      email: user.value?.email || "",
      companyName: user.value?.company_name || "",
    });

    const passwordChange = ref({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });

    // Update profile whenever user data changes
    if (user.value) {
      profile.value = {
        firstName: user.value.first_name,
        lastName: user.value.last_name,
        email: user.value.email,
        companyName: user.value.company_name || "",
      };
    }

    const updateProfile = async () => {
      try {
        loading.value = true;
        error.value = "";
        success.value = "";

        // Validate password change if attempted
        if (passwordChange.value.newPassword) {
          if (
            passwordChange.value.newPassword !==
            passwordChange.value.confirmPassword
          ) {
            error.value = t("profile.messages.passwordMismatch");
            return;
          }
          if (!passwordChange.value.currentPassword) {
            error.value = t("profile.messages.currentPasswordRequired");
            return;
          }

          // Update password using store action
          await authStore.changePassword({
            old_password: passwordChange.value.currentPassword,
            new_password: passwordChange.value.newPassword,
            new_password2: passwordChange.value.confirmPassword,
          });
        }

        // Update profile information using store action
        await authStore.updateUserProfile({
          first_name: profile.value.firstName,
          last_name: profile.value.lastName,
          company_name: profile.value.companyName,
        });

        success.value = t("profile.messages.success");

        // Clear password fields after successful update
        passwordChange.value = {
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        };
      } catch (err: any) {
        // Handle specific error messages from the backend
        if (err.response?.data?.detail) {
          error.value = err.response.data.detail;
        } else if (err.response?.data?.message) {
          error.value = err.response.data.message;
        } else {
          error.value = t("profile.messages.updateFailed");
        }
      } finally {
        loading.value = false;
      }
    };

    return {
      profile,
      passwordChange,
      loading,
      error,
      success,
      updateProfile,
    };
  },
});
</script>

<style scoped>
.card {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.form-control {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.form-control:focus {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.btn-primary {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  transition: filter 0.2s;
  border-radius: 2px;
}

.btn-primary:hover:not(:disabled) {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  filter: brightness(0.9);
}
</style>
